import { useEffect, useMemo, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { X, ChevronLeft, ChevronRight, Lightbulb, Check } from 'lucide-react';
import toast from 'react-hot-toast';
import { ideaApi, workItemApi } from '../../services/api';
import { useProjectStore } from '../../store/useProjectStore';
import { useOrgHierarchyStore } from '../../store/useOrgHierarchyStore';
import type { IdeaLevel, WorkItem } from '../../types';

interface Props {
  /** When launched from a project, pre-selects and locks the project. */
  lockedProjectId?: string;
  onClose: () => void;
  onCreated: (idea: WorkItem) => void;
}

const LEVELS: { id: IdeaLevel; label: string; hint: string }[] = [
  { id: 'epic', label: 'Epic', hint: 'a large new capability in a project' },
  { id: 'feature', label: 'Feature', hint: 'part of an existing epic' },
  { id: 'project', label: 'Project', hint: 'a brand-new project' },
  { id: 'program', label: 'Training programme', hint: 'a new model to train' },
];

const STEPS = ['Capability', 'Hypothesis', 'Outcomes', 'Alignment', 'Sizing', 'Review'] as const;

// WSJF inputs are scored on the Fibonacci scale (the backend rejects other values).
const FIB = [1, 2, 3, 5, 8, 13, 21];

// SAFe Epic Hypothesis Statement scaffold — stored in customFields.
const emptyForm = {
  title: '',
  description: '',
  // hypothesis
  forWhom: '',
  theProblem: '',
  theSolution: '',
  theBenefit: '',
  unlike: '',
  // outcomes
  leadingIndicators: '',
  successMetrics: '',
  // alignment
  projectId: '',
  ideaLevel: 'epic' as IdeaLevel,
  parentEpicId: '',
  targetOrgId: '',      // project-level: org the new project will live in
  targetProgramId: '',  // project-level: optional program
  // program-level (a new Model training programme, EP-26 FE-9): the model step
  modelPurpose: '',
  unitName: '',
  unitLabel: '',
  candidateModels: '',
  mainScoreName: '',
  mainScoreMin: '',
  unsafeMax: '',
  dataSources: '',
  constraints: '',
  // sizing (WSJF)
  businessValue: 5,
  timeCriticality: 5,
  riskReductionOpportunity: 5,
  jobSize: 5,
};

export default function IdeaWizard({ lockedProjectId, onClose, onCreated }: Props) {
  const { projects } = useProjectStore();
  const { organizations, programs, fetchOrganizations, fetchPrograms } = useOrgHierarchyStore();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ ...emptyForm, projectId: lockedProjectId || '' });
  const [epics, setEpics] = useState<WorkItem[]>([]);

  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  // Org + program lists power the project-level alignment step.
  useEffect(() => {
    if (organizations.length === 0) fetchOrganizations();
    if (programs.length === 0) fetchPrograms();
  }, [organizations.length, programs.length, fetchOrganizations, fetchPrograms]);

  const orgPrograms = useMemo(
    () => programs.filter((p) => p.organizationId === form.targetOrgId),
    [programs, form.targetOrgId]
  );

  // Load epics for the chosen project when a feature-level idea needs a parent.
  useEffect(() => {
    if (form.ideaLevel === 'feature' && form.projectId) {
      workItemApi
        .list({ projectId: form.projectId, workItemType: 'epic' })
        .then((rows) => setEpics(rows || []))
        .catch(() => setEpics([]));
    }
  }, [form.ideaLevel, form.projectId]);

  const wsjf = useMemo(() => {
    const size = form.jobSize || 0;
    if (!size) return 0;
    return (form.businessValue + form.timeCriticality + form.riskReductionOpportunity) / size;
  }, [form]);

  const canNext = useMemo(() => {
    switch (step) {
      case 0: return form.title.trim().length > 0;
      case 3:
        if (form.ideaLevel === 'program') return !!form.targetOrgId && !!form.unitName.trim() && form.mainScoreMin !== '' && form.unsafeMax !== '';
        if (form.ideaLevel === 'project') return !!form.targetOrgId;
        return !!form.projectId && (form.ideaLevel === 'epic' || !!form.parentEpicId);
      default: return true;
    }
  }, [step, form]);

  // Project- and programme-level ideas propose something new under an org, not in a project.
  const orgLevel = form.ideaLevel === 'project' || form.ideaLevel === 'program';

  const buildPayload = (ideaStatus: 'draft' | 'submitted') => ({
    // Project-level ideas propose a NEW project, so they carry a target org/
    // program instead of a host projectId.
    projectId: orgLevel ? '' : form.projectId,
    targetOrgId: orgLevel ? form.targetOrgId : '',
    targetProgramId: form.ideaLevel === 'project' ? form.targetProgramId : '',
    title: form.title.trim(),
    description: form.description.trim(),
    ideaLevel: form.ideaLevel,
    ideaStatus,
    businessValue: form.businessValue,
    timeCriticality: form.timeCriticality,
    riskReductionOpportunity: form.riskReductionOpportunity,
    jobSize: form.jobSize,
    customFields: {
      hypothesis: {
        forWhom: form.forWhom,
        theProblem: form.theProblem,
        theSolution: form.theSolution,
        theBenefit: form.theBenefit,
        unlike: form.unlike,
      },
      leadingIndicators: form.leadingIndicators,
      successMetrics: form.successMetrics,
      // Parent-epic hint for feature-level ideas; the approver confirms it.
      // (Kept in customFields because the schema rejects unknown top-level keys.)
      parentEpicHint: form.ideaLevel === 'feature' ? form.parentEpicId : '',
      ...(form.ideaLevel === 'program' ? { trainingIdea: {
        purpose: form.modelPurpose.trim(),
        unit: { name: form.unitName.trim(), label: form.unitLabel.trim() || form.unitName.trim() },
        candidateModels: form.candidateModels.split(',').map((x) => x.trim()).filter(Boolean),
        metrics: { mainScoreName: form.mainScoreName.trim(), mainScoreMin: Number(form.mainScoreMin), unsafeMax: Number(form.unsafeMax) },
        dataSources: form.dataSources.split(',').map((x) => x.trim()).filter(Boolean),
        constraints: form.constraints.trim(),
      } } : {}),
    },
  });

  const submit = async (ideaStatus: 'draft' | 'submitted') => {
    if (orgLevel ? !form.targetOrgId : !form.projectId) {
      toast.error(orgLevel
        ? 'Choose the organization for the proposal.'
        : 'Select a project to align this idea with.');
      setStep(3);
      return;
    }
    setSaving(true);
    try {
      const idea = await ideaApi.create(buildPayload(ideaStatus));
      toast.success(ideaStatus === 'draft' ? 'Idea saved as draft' : 'Idea submitted for review');
      onCreated(idea);
    } catch (e: any) {
      toast.error(e?.message || 'Failed to create idea');
    } finally {
      setSaving(false);
    }
  };

  const input =
    'w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-sm focus:ring-2 focus:ring-accent focus:border-accent outline-none';
  const label = 'block text-xs font-semibold text-gray-600 dark:text-gray-300 mb-1';

  return (
    <Dialog open onClose={onClose} className="relative z-50">
      <div className="fixed inset-0 bg-black/40" aria-hidden="true" />
      <div className="fixed inset-0 flex items-center justify-center p-4">
        <Dialog.Panel className="w-full max-w-2xl bg-white dark:bg-gray-900 rounded-xl shadow-2xl flex flex-col max-h-[90vh]">
          {/* Header */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center gap-2">
              <Lightbulb className="text-amber-500" size={20} />
              <Dialog.Title className="text-base font-semibold">New Idea</Dialog.Title>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200">
              <X size={20} />
            </button>
          </div>

          {/* Stepper */}
          <div className="flex items-center gap-1 px-5 py-3 border-b border-gray-100 dark:border-gray-800 overflow-x-auto">
            {STEPS.map((s, i) => (
              <div key={s} className="flex items-center gap-1 shrink-0">
                <div
                  className={`flex items-center justify-center w-6 h-6 rounded-full text-xs font-bold ${
                    i < step ? 'bg-accent text-white' : i === step ? 'bg-accent/20 text-accent ring-2 ring-accent' : 'bg-gray-100 dark:bg-gray-800 text-gray-400'
                  }`}
                >
                  {i < step ? <Check size={12} /> : i + 1}
                </div>
                <span className={`text-xs ${i === step ? 'font-semibold text-accent' : 'text-gray-400'}`}>{s}</span>
                {i < STEPS.length - 1 && <ChevronRight size={12} className="text-gray-300 dark:text-gray-600" />}
              </div>
            ))}
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">
            {step === 0 && (
              <>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Describe the new capability you're proposing. Keep the title outcome-oriented.
                </p>
                <div>
                  <label className={label}>Capability title *</label>
                  <input className={input} value={form.title} onChange={(e) => set('title', e.target.value)} placeholder="e.g. Self-service partner onboarding" />
                </div>
                <div>
                  <label className={label}>Summary</label>
                  <textarea className={input} rows={4} value={form.description} onChange={(e) => set('description', e.target.value)} placeholder="What is it, at a glance?" />
                </div>
              </>
            )}

            {step === 1 && (
              <>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  SAFe Epic Hypothesis Statement — frame the bet you're making.
                </p>
                <div>
                  <label className={label}>For (target user / segment)</label>
                  <input className={input} value={form.forWhom} onChange={(e) => set('forWhom', e.target.value)} placeholder="partner administrators" />
                </div>
                <div>
                  <label className={label}>Who (have the problem of…)</label>
                  <input className={input} value={form.theProblem} onChange={(e) => set('theProblem', e.target.value)} placeholder="cannot onboard their own teams without support" />
                </div>
                <div>
                  <label className={label}>The (solution) is a…</label>
                  <input className={input} value={form.theSolution} onChange={(e) => set('theSolution', e.target.value)} placeholder="guided self-service onboarding flow" />
                </div>
                <div>
                  <label className={label}>That (delivers this benefit)</label>
                  <input className={input} value={form.theBenefit} onChange={(e) => set('theBenefit', e.target.value)} placeholder="cuts onboarding time from days to minutes" />
                </div>
                <div>
                  <label className={label}>Unlike (current alternative)</label>
                  <input className={input} value={form.unlike} onChange={(e) => set('unlike', e.target.value)} placeholder="manual, ticket-driven onboarding" />
                </div>
              </>
            )}

            {step === 2 && (
              <>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  How will we know this capability is working? Define the measures up front.
                </p>
                <div>
                  <label className={label}>Leading indicators</label>
                  <textarea className={input} rows={3} value={form.leadingIndicators} onChange={(e) => set('leadingIndicators', e.target.value)} placeholder="early signals during the PI (adoption, activation…)" />
                </div>
                <div>
                  <label className={label}>Success metrics</label>
                  <textarea className={input} rows={3} value={form.successMetrics} onChange={(e) => set('successMetrics', e.target.value)} placeholder="target outcomes (e.g. -80% onboarding time)" />
                </div>
              </>
            )}

            {step === 3 && (
              <>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Choose the level of the capability. Epic/Feature align with an existing project; Project proposes a brand-new project.
                </p>
                <div>
                  <label className={label}>Level</label>
                  <div className="flex gap-2">
                    {LEVELS.map((lvl) => (
                      <button
                        key={lvl.id}
                        type="button"
                        onClick={() => set('ideaLevel', lvl.id)}
                        className={`flex-1 px-3 py-2 rounded-lg border text-sm ${
                          form.ideaLevel === lvl.id ? 'border-accent bg-accent/10 text-accent font-semibold' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300'
                        }`}
                      >
                        {lvl.label}
                        <span className="block text-[10px] font-normal opacity-70">{lvl.hint}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Epic / Feature: align with an existing project */}
                {!orgLevel && (
                  <div>
                    <label className={label}>Project *</label>
                    <select
                      className={input}
                      value={form.projectId}
                      disabled={!!lockedProjectId}
                      onChange={(e) => set('projectId', e.target.value)}
                    >
                      <option value="">Select a project…</option>
                      {projects.map((p) => (
                        <option key={p._id} value={p._id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                )}

                {form.ideaLevel === 'feature' && (
                  <div>
                    <label className={label}>Parent epic *</label>
                    <select className={input} value={form.parentEpicId} onChange={(e) => set('parentEpicId', e.target.value)}>
                      <option value="">Select an epic…</option>
                      {epics.map((ep) => (
                        <option key={ep._id} value={ep._id}>{ep.displayId ? `${ep.displayId} — ` : ''}{ep.title}</option>
                      ))}
                    </select>
                    {epics.length === 0 && (
                      <p className="text-xs text-amber-600 mt-1">No epics in this project yet — the approver can create a parent epic at approval.</p>
                    )}
                  </div>
                )}

                {/* Project level: target org (+ optional program) for the new project */}
                {orgLevel && (
                  <>
                    <div>
                      <label className={label}>Organization *</label>
                      <select
                        className={input}
                        value={form.targetOrgId}
                        onChange={(e) => { set('targetOrgId', e.target.value); set('targetProgramId', ''); }}
                      >
                        <option value="">Select an organization…</option>
                        {organizations.map((o) => (
                          <option key={o._id} value={o._id}>{o.name}</option>
                        ))}
                      </select>
                    </div>
                    {form.ideaLevel === 'project' && <div>
                      <label className={label}>Program (optional)</label>
                      <select
                        className={input}
                        value={form.targetProgramId}
                        disabled={!form.targetOrgId}
                        onChange={(e) => set('targetProgramId', e.target.value)}
                      >
                        <option value="">No program</option>
                        {orgPrograms.map((p) => (
                          <option key={p._id} value={p._id}>{p.name}</option>
                        ))}
                      </select>
                      <p className="text-xs text-gray-400 mt-1">The approver confirms org, program and PI when creating the project.</p>
                    </div>}
                  </>
                )}

                {/* Programme level: the model step (EP-26 FE-9 ST-1) */}
                {form.ideaLevel === 'program' && (
                  <div className="space-y-3 rounded-lg border border-gray-200 dark:border-gray-700 p-3">
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">Model</div>
                    <div>
                      <label className={label}>Purpose</label>
                      <textarea className={input} rows={2} value={form.modelPurpose} onChange={(e) => set('modelPurpose', e.target.value)} placeholder="What the model is for" />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className={label}>Unit it learns *</label>
                        <input className={input} value={form.unitName} onChange={(e) => set('unitName', e.target.value)} placeholder="e.g. atom, skill" />
                      </div>
                      <div>
                        <label className={label}>Unit label</label>
                        <input className={input} value={form.unitLabel} onChange={(e) => set('unitLabel', e.target.value)} placeholder="e.g. Atom" />
                      </div>
                    </div>
                    <div>
                      <label className={label}>Candidate models</label>
                      <input className={input} value={form.candidateModels} onChange={(e) => set('candidateModels', e.target.value)} placeholder="comma-separated base models" />
                    </div>
                    <div className="grid grid-cols-3 gap-2">
                      <div>
                        <label className={label}>Main metric</label>
                        <input className={input} value={form.mainScoreName} onChange={(e) => set('mainScoreName', e.target.value)} placeholder="e.g. within-1" />
                      </div>
                      <div>
                        <label className={label}>Pass mark (0-1) *</label>
                        <input className={input} type="number" min={0} max={1} step={0.01} value={form.mainScoreMin} onChange={(e) => set('mainScoreMin', e.target.value)} placeholder="0.85" />
                      </div>
                      <div>
                        <label className={label}>Max unsafe (0-1) *</label>
                        <input className={input} type="number" min={0} max={1} step={0.01} value={form.unsafeMax} onChange={(e) => set('unsafeMax', e.target.value)} placeholder="0" />
                      </div>
                    </div>
                    <div>
                      <label className={label}>Data sources</label>
                      <input className={input} value={form.dataSources} onChange={(e) => set('dataSources', e.target.value)} placeholder="comma-separated" />
                    </div>
                    <div>
                      <label className={label}>Constraints</label>
                      <input className={input} value={form.constraints} onChange={(e) => set('constraints', e.target.value)} placeholder="privacy, compute, licensing" />
                    </div>
                  </div>
                )}
              </>
            )}

            {step === 4 && (
              <>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Rough WSJF sizing helps prioritize the idea against other work. This carries over to the spawned epic/feature.
                </p>
                {([
                  ['businessValue', 'Business Value'],
                  ['timeCriticality', 'Time Criticality'],
                  ['riskReductionOpportunity', 'Risk Reduction / Opportunity'],
                  ['jobSize', 'Job Size'],
                ] as const).map(([k, lbl]) => (
                  <div key={k}>
                    <label className={label}>{lbl}</label>
                    <div className="flex gap-1.5">
                      {FIB.map((n) => (
                        <button
                          key={n}
                          type="button"
                          onClick={() => set(k, n)}
                          className={`w-9 h-9 rounded-lg text-sm font-semibold border ${
                            form[k] === n ? 'border-accent bg-accent text-white' : 'border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:border-accent'
                          }`}
                        >
                          {n}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
                <div className="rounded-lg bg-accent/10 px-4 py-3 text-sm">
                  Estimated WSJF score: <span className="font-bold text-accent">{wsjf.toFixed(2)}</span>
                </div>
              </>
            )}

            {step === 5 && (
              <div className="space-y-3 text-sm">
                <Row k="Capability" v={form.title} />
                <Row k="Level" v={form.ideaLevel} />
                {form.ideaLevel === 'program' ? (
                  <>
                    <Row k="New programme in org" v={organizations.find((o) => o._id === form.targetOrgId)?.name || '—'} />
                    <Row k="Model" v={`${form.unitName || '...'} units; pass ${form.mainScoreName || 'main score'} at least ${form.mainScoreMin || '...'}, unsafe at most ${form.unsafeMax || '...'}`} />
                  </>
                ) : form.ideaLevel === 'project' ? (
                  <>
                    <Row k="New project in org" v={organizations.find((o) => o._id === form.targetOrgId)?.name || '—'} />
                    <Row k="Program" v={orgPrograms.find((p) => p._id === form.targetProgramId)?.name || '(none / set at approval)'} />
                  </>
                ) : (
                  <Row k="Project" v={projects.find((p) => p._id === form.projectId)?.name || '—'} />
                )}
                {form.ideaLevel === 'feature' && (
                  <Row k="Parent epic" v={epics.find((e) => e._id === form.parentEpicId)?.title || '(create at approval)'} />
                )}
                <Row k="Hypothesis" v={`For ${form.forWhom || '…'} who ${form.theProblem || '…'}, the ${form.theSolution || '…'} delivers ${form.theBenefit || '…'}.`} />
                <Row k="Success metrics" v={form.successMetrics || '—'} />
                <Row k="WSJF" v={`${wsjf.toFixed(2)} (BV ${form.businessValue} / TC ${form.timeCriticality} / RR ${form.riskReductionOpportunity} / Size ${form.jobSize})`} />
                <p className="text-xs text-gray-500 dark:text-gray-400 pt-2">
                  Save as a draft to keep working on it, or submit it into the review pipeline.
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex items-center justify-between px-5 py-4 border-t border-gray-200 dark:border-gray-700">
            <button
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0}
              className="flex items-center gap-1 px-3 py-2 text-sm text-gray-600 dark:text-gray-300 disabled:opacity-40"
            >
              <ChevronLeft size={16} /> Back
            </button>
            {step < STEPS.length - 1 ? (
              <button
                onClick={() => setStep((s) => Math.min(STEPS.length - 1, s + 1))}
                disabled={!canNext}
                className="flex items-center gap-1 px-4 py-2 text-sm font-semibold bg-accent text-white rounded-lg disabled:opacity-40"
              >
                Next <ChevronRight size={16} />
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <button onClick={() => submit('draft')} disabled={saving} className="px-4 py-2 text-sm font-semibold border border-gray-300 dark:border-gray-600 rounded-lg disabled:opacity-40">
                  Save draft
                </button>
                <button onClick={() => submit('submitted')} disabled={saving} className="px-4 py-2 text-sm font-semibold bg-accent text-white rounded-lg disabled:opacity-40">
                  Submit for review
                </button>
              </div>
            )}
          </div>
        </Dialog.Panel>
      </div>
    </Dialog>
  );
}

function Row({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex gap-3">
      <span className="w-32 shrink-0 text-xs font-semibold text-gray-500 dark:text-gray-400 pt-0.5">{k}</span>
      <span className="flex-1 text-gray-800 dark:text-gray-200">{v}</span>
    </div>
  );
}
